import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { TypeAnimation } from 'react-type-animation';

import emailjs from '@emailjs/browser';

import {
  LuLayoutDashboard, LuServer, LuSmartphone, LuCloud, LuCode2, LuPenTool,
  LuArrowUpRight, LuArrowDown, LuArrowUp, LuMail, LuPhone, LuSend, LuLoader,
  LuGraduationCap, LuQuote, LuMapPin, LuScrollText, LuCalendar
} from 'react-icons/lu';
import {
  FaLinkedinIn, FaGithub, FaXTwitter, FaInstagram, FaFacebookF, FaMedium, FaWhatsapp
} from 'react-icons/fa6';

import Reveal from './Reveal';
import Projects from './Projects';
import Experience from './Experience';
import Toast from './Toast';
import { SOCIALS, STATS, MARQUEE, EXPERTISE, EDUCATION, PUBLICATIONS, REFERENCES } from './Content_data';

import './styles.scss';

import ProfileImage from './Assets/profile_image.png';

const SERVICE_ID = "service_sutmx9v";
const TEMPLATE_ID = "template_hnlser7";
const PUBLIC_KEY = "8kH6I3hza7g1Uq5Yo";

const WHATSAPP_URL = 'https://api.whatsapp.com/send?phone=+94702010059&text=Hi, Isuru. I found your contact on your website and I would like to get in touch with you.';

const EXPERTISE_ICONS = {
  frontend: <LuLayoutDashboard />,
  backend: <LuServer />,
  mobile: <LuSmartphone />,
  cloud: <LuCloud />,
  languages: <LuCode2 />,
  design: <LuPenTool />,
};

const SOCIAL_ICONS = {
  LinkedIn: <FaLinkedinIn />,
  Github: <FaGithub />,
  Twitter: <FaXTwitter />,
  Medium: <FaMedium />,
  Instagram: <FaInstagram />,
  Facebook: <FaFacebookF />,
};

function App() {
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const form = useRef();
  const footer = useRef();

  useEffect(() => {
    if (!toast || toast.type === 'loading') return;

    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const node = footer.current;
    if (!node) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsFooterVisible(entry.isIntersecting);
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    setToast({ type: 'loading', message: 'Sending your message...' });

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, e.target, PUBLIC_KEY)
      .then(() => {
        setToast({ type: 'success', message: 'Message sent. I will get back to you soon.' });
        setLoading(false);
        form.current.reset();
      }, () => {
        setToast({ type: 'error', message: 'Failed to send the message. Please try again later.' });
        setLoading(false);
      });
  };

  return (
    <div className="App">
      <Helmet>
        <title>Isuru Ariyarathna | Software Engineer | Full Stack Developer</title>
        <meta name="description" content="Isuru Ariyarathna - Software Engineer specializing in React.js, Node.js, React Native, Azure, and AWS. Building scalable web and mobile applications for clients worldwide." />
        <link rel="canonical" href="https://isuruariyarathna.me/" />
      </Helmet>

      <Toast toast={toast} />

      <a
        className={`floating-chat ${isFooterVisible ? 'is-hidden' : ''}`}
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp />
        <span className="label">Chat with me</span>
      </a>

      <section className="hero" id="hero">
        <div className="hero-backdrop">
          <span className="orb orb--one"></span>
          <span className="orb orb--two"></span>
          <span className="orb orb--three"></span>
          <span className="grid"></span>
          <span className="vignette"></span>
        </div>

        <div className="page hero-inner">
          <Reveal className="hero-badge" variant="scale">
            <span className="pulse"></span> Available for new projects
          </Reveal>

          <Reveal as="h1" className="hero-title" delay={80}>
            Isuru
            <br />
            Ariyarathna<span className="dot">.</span>
          </Reveal>

          <Reveal className="hero-type" delay={160}>
            <TypeAnimation
              sequence={[
                "Software Engineer",
                1600,
                "Full Stack Developer",
                1600,
                "I build things for web & mobile",
                2200,
              ]}
              wrapper="span"
              cursor={true}
              repeat={Infinity}
            />
          </Reveal>

          <Reveal as="p" className="hero-lead" delay={220}>
            I design and build scalable web and mobile products - from the interface
            people touch to the cloud infrastructure that keeps it running.
          </Reveal>

          <Reveal className="hero-actions" delay={280}>
            <a className="btn btn--primary" href="#myWork">
              View my work <LuArrowUpRight />
            </a>
            <a className="btn btn--ghost" href="#getintouch">
              Get in touch
            </a>
          </Reveal>

          <Reveal className="hero-socials" delay={340}>
            {SOCIALS.map(social => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
              >
                {SOCIAL_ICONS[social.name]}
              </a>
            ))}
          </Reveal>
        </div>

        <a className="hero-scroll" href="#about" aria-label="Scroll to about section">
          <LuArrowDown />
        </a>
      </section>

      <div className="marquee">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map((item, index) => (
            <span key={index}>{item}<i>✦</i></span>
          ))}
        </div>
      </div>

      <section className="section about" id="about">
        <div className="page about-grid">
          <div className="about-copy">
            <Reveal className="section-head">
              <span className="eyebrow">01 - About me</span>
              <h2>Engineering with <span className="gradient-text">intent</span>, not just output.</h2>
            </Reveal>

            <Reveal as="p" delay={80}>
              I'm Isuru Ariyarathna, a software engineer holding a BEng (Hons) First Class
              in Software Engineering from the University of Westminster. I build full stack products
              with a strong foundation in computer science and a bias towards clean,
              maintainable systems.
            </Reveal>

            <Reveal as="p" delay={140}>
              My interest in programming started at 14, driven by curiosity about how
              computers solve real-world problems. Today I focus on backend architecture,
              mobile application development and cloud platforms, and I'm always picking
              up whatever the next problem demands.
            </Reveal>

            <Reveal className="about-stats" delay={200}>
              {STATS.map(stat => (
                <div className="stat" key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </Reveal>
          </div>

          <Reveal className="about-visual" variant="right" delay={120}>
            <div className="about-photo">
              <img src={ProfileImage} alt="Isuru Ariyarathna" />
            </div>
            <div className="about-glow"></div>
          </Reveal>
        </div>
      </section>

      <section className="section expertise" id="expertise">
        <div className="page">
          <Reveal className="section-head">
            <span className="eyebrow">02 - Expertise</span>
            <h2>What I work with</h2>
            <p>A full stack toolkit shaped by shipping production software across web, mobile and cloud.</p>
          </Reveal>

          <div className="expertise-grid">
            {EXPERTISE.map((item, index) => (
              <Reveal className="expertise-card" key={item.title} delay={index * 60}>
                <span className="icon">{EXPERTISE_ICONS[item.icon]}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="chips">
                  {item.items.map(skill => (
                    <span key={skill}>{skill}</span>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section experience" id="experience">
        <div className="page">
          <Reveal className="section-head">
            <span className="eyebrow">03 - Experience</span>
            <h2>Where I've been</h2>
            <p>Roles across product engineering, architecture and technology leadership.</p>
          </Reveal>

          <Experience />
        </div>
      </section>

      <section className="section education" id="education">
        <div className="page">
          <Reveal className="section-head">
            <span className="eyebrow">04 - Education</span>
            <h2>Academic background</h2>
          </Reveal>

          <div className="education-list">
            {EDUCATION.map((item, index) => (
              <Reveal className="education-item" key={item.qualification} delay={index * 80}>
                <span className="marker"><LuGraduationCap /></span>
                <div className="education-body">
                  <span className="period">{item.period}</span>
                  <h3>{item.qualification}</h3>
                  <p className="institute">{item.institute}</p>
                  <p className="location"><LuMapPin /> {item.location}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section myWork" id="myWork">
        <div className="page">
          <Reveal className="section-head">
            <span className="eyebrow">05 - Selected work</span>
            <h2>Things I've built</h2>
            <p>Client platforms, mobile apps and internal tools delivered end to end.</p>
          </Reveal>

          <Projects />
        </div>
      </section>

      <section className="section publications" id="publications">
        <div className="page">
          <Reveal className="section-head">
            <span className="eyebrow">06 - Publications</span>
            <h2>Research &amp; publications</h2>
            <p>Peer-reviewed work on cloud automation and multi-agent systems.</p>
          </Reveal>

          <div className="publications-list">
            {PUBLICATIONS.map((item, index) => (
              <Reveal
                as="a"
                className="publication-card"
                key={item.title}
                delay={index * 80}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="publication-icon"><LuScrollText /></span>

                <div className="publication-body">
                  <div className="publication-meta">
                    <span className="publisher">{item.publisher}</span>
                    <span><LuCalendar /> {item.year}</span>
                    <span><LuMapPin /> {item.location}</span>
                  </div>

                  <h3>{item.title}</h3>
                  <p className="venue">{item.venue}</p>
                  <p className="brief">{item.brief}</p>

                  <div className="publication-tags">
                    {item.tags.map(tag => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>

                  <span className="publication-link">
                    View on {item.publisher} <LuArrowUpRight />
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section references" id="references">
        <div className="page">
          <Reveal className="section-head">
            <span className="eyebrow">07 - References</span>
            <h2>People I've worked with</h2>
          </Reveal>

          <div className="references-grid">
            {REFERENCES.map((item, index) => (
              <Reveal className="reference-card" key={item.name} delay={index * 80}>
                <span className="quote"><LuQuote /></span>
                <div className="reference-head">
                  <div className="avatar">
                    <img src={item.image} alt={item.name} />
                  </div>
                  <div>
                    <h3>{item.name}</h3>
                    <ul>
                      {item.positions.map(position => (
                        <li key={position}>{position}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="reference-contacts">
                  <a href={`tel:${item.contact}`}><LuPhone /> {item.contact}</a>
                  <a href={`mailto:${item.email}`}><LuMail /> {item.email}</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section getintouch" id="getintouch">
        <div className="page">
          <Reveal className="section-head">
            <span className="eyebrow">08 - Contact</span>
            <h2>Let's build something great</h2>
          </Reveal>

          <div className="contact-grid">
            <Reveal className="contact-copy" variant="left">
              <p>
                I collaborate with clients worldwide to architect and deliver full stack
                applications that solve real problems. If you have a product in mind, or
                just want to talk through an idea, my inbox is open.
              </p>

              <div className="contact-methods">
                <a href="tel:+94702010059">
                  <span className="icon"><LuPhone /></span>
                  <span className="detail">
                    <small>Phone</small>
                    +94 70 20100 59
                  </span>
                  <LuArrowUpRight className="arrow" />
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <span className="icon"><FaWhatsapp /></span>
                  <span className="detail">
                    <small>WhatsApp</small>
                    +94 70 20100 59
                  </span>
                  <LuArrowUpRight className="arrow" />
                </a>
                <a href="mailto:isuruariyarathna2k00@gmail.com">
                  <span className="icon"><LuMail /></span>
                  <span className="detail">
                    <small>Email</small>
                    isuruariyarathna2k00@gmail.com
                  </span>
                  <LuArrowUpRight className="arrow" />
                </a>
              </div>
            </Reveal>

            <Reveal className="contact-form" variant="right" delay={100}>
              <form ref={form} onSubmit={sendEmail}>
                <div className="field">
                  <input required type="text" name="user_name" id="user_name" placeholder=" " />
                  <label htmlFor="user_name">Your name</label>
                </div>
                <div className="field">
                  <input required type="email" name="user_email" id="user_email" placeholder=" " />
                  <label htmlFor="user_email">Email address</label>
                </div>
                <div className="field">
                  <textarea required name="message" id="message" rows="5" placeholder=" " />
                  <label htmlFor="message">Tell me about your project</label>
                </div>
                <button className="btn btn--primary" type="submit" disabled={loading}>
                  {loading
                    ? <>Sending <LuLoader className="spin" /></>
                    : <>Send message <LuSend /></>}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <footer className="site-footer" ref={footer}>
        <div className="page">
          <div className="footer-top">
            <div className="footer-brand">
              <h2>Isuru<span className="dot">.</span></h2>
              <p>Software Engineer building for web, mobile and cloud.</p>
            </div>

            <div className="footer-socials">
              {SOCIALS.map(social => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                >
                  {SOCIAL_ICONS[social.name]}
                </a>
              ))}
            </div>
          </div>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} Isuru Ariyarathna. All rights reserved.</p>
            <button
              className="to-top"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label="Back to top"
            >
              Back to top <LuArrowUp />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
